<script setup lang="ts">
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuth } from '@/composables/useAuth'
import { resendVerificationEmail } from '@/api/auth'
import AppFooter from '@/components/layout/AppFooter.vue'
import AppSpinner from '@/components/shared/AppSpinner.vue'

const { t } = useI18n()
const router = useRouter()
const { user, logout } = useAuth()

const resendLoading = ref(false)
const resendSent = ref(false)
const error = ref<string | null>(null)

async function handleResend(): Promise<void> {
  resendLoading.value = true
  error.value = null
  try {
    await resendVerificationEmail()
    resendSent.value = true
  } catch {
    error.value = t('auth.verify_email.resend_error')
  } finally {
    resendLoading.value = false
  }
}

async function handleLogout(): Promise<void> {
  await logout()
  router.push('/organizer/login')
}
</script>

<template>
  <div class="min-h-screen bg-slate-900 flex flex-col">
    <div class="flex-1 flex items-center justify-center px-4 py-12">
    <div class="w-full max-w-md">
      <!-- Wordmark -->
      <div class="text-center mb-8">
        <a href="/" class="inline-flex items-center gap-2 group">
          <span class="text-2xl font-black text-white tracking-tight">Startline</span>
          <span class="w-2 h-2 rounded-full bg-emerald-400 mb-0.5" />
        </a>
      </div>

      <div class="bg-slate-800/50 border border-slate-700 rounded-2xl p-8 text-center">
        <!-- Icon -->
        <div class="mx-auto mb-4 w-14 h-14 rounded-full bg-emerald-500/10 flex items-center justify-center">
          <svg class="w-7 h-7 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        </div>

        <h1 class="text-xl font-bold text-white mb-2">{{ t('auth.verify_email.heading') }}</h1>
        <p class="text-slate-400 text-sm mb-6">
          {{ t('auth.verify_email.instructions', { email: user?.email ?? '' }) }}
        </p>

        <div v-if="resendSent" class="mb-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30 px-4 py-3 text-sm text-emerald-400">
          {{ t('auth.verify_email.resend_sent') }}
        </div>

        <div v-if="error" class="mb-4 rounded-lg bg-red-500/10 border border-red-500/30 px-4 py-3 text-sm text-red-400">
          {{ error }}
        </div>

        <button
          type="button"
          :disabled="resendLoading || resendSent"
          class="w-full bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg px-4 py-2.5 text-sm transition-colors flex items-center justify-center gap-2 mb-4"
          @click="handleResend"
        >
          <AppSpinner v-if="resendLoading" size="button" />
          {{ t('auth.verify_email.resend_button') }}
        </button>

        <button
          type="button"
          class="text-sm text-slate-400 hover:text-slate-300 transition-colors"
          @click="handleLogout"
        >
          {{ t('auth.logout.button') }}
        </button>
      </div>
    </div>
    </div>
    <AppFooter />
  </div>
</template>
